import { AppearanceTypes, AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import InfoIcon from '@atlaskit/icon/core/migration/information--info';
import SuccessIcon from '@atlaskit/icon/core/migration/success--check-circle';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { token } from '@atlaskit/tokens';
import React, { createContext, ReactNode, useContext, useState } from 'react';

import { FlagsProviderProps, AddFlagProps, Flag, FlagContextType } from './types';

const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

const FlagContext = createContext<FlagContextType | undefined>(undefined);

export const useFlags = () => {
  const context = useContext(FlagContext);

  if (!context) {
    throw new Error('useFlags must be used within a FlagsProvider');
  }

  return context;
};

export const FlagsProvider = ({ children }: FlagsProviderProps) => {
  const [flags, setFlags] = useState<Flag[]>([]);

  const addFlag = ({ title, description, actions, appearance, icon }: AddFlagProps) => {
    setFlags((prevFlags) => {
      const newFlagId = prevFlags.length + 1;
      const newFlags = [
        { id: newFlagId, title, description, actions, appearance, icon },
        ...prevFlags,
      ];
      return newFlags;
    });
  };

  const getIcon = (appearance: AppearanceTypes): ReactNode => {
    switch (appearance) {
      case 'info':
        return (
          <InfoIcon
            spacing="spacious"
            label=""
            LEGACY_secondaryColor={token('color.background.neutral.bold')}
          />
        );
      case 'warning':
        return (
          <WarningIcon
            spacing="spacious"
            label=""
            LEGACY_secondaryColor={token('color.background.warning.bold')}
          />
        );
      case 'error':
        return (
          <ErrorIcon
            spacing="spacious"
            label=""
            LEGACY_secondaryColor={token('color.background.danger.bold')}
          />
        );
      case 'success':
        return (
          <SuccessIcon
            spacing="spacious"
            label=""
            LEGACY_secondaryColor={token('color.background.success.bold')}
          />
        );
      case 'normal':
        return (
          <InfoIcon spacing="spacious" label="" color={token('color.icon.information')} />
        );
      default:
        unreachable(appearance);
    }
  };

  const handleDismiss = () => {
    setFlags(flags.slice(1));
  };

  return (
    <FlagContext.Provider value={{ addFlag }}>
      {children}
      <FlagGroup onDismissed={handleDismiss}>
        {flags.map((flag: Flag) => {
          const flagAppearance = flag.appearance ?? 'normal';
          return (
            <AutoDismissFlag
              appearance={flagAppearance}
              id={flag.id}
              icon={flag.icon ?? getIcon(flagAppearance)}
              key={flag.id}
              title={flag.title}
              description={flag.description}
              actions={flag.actions}
            />
          );
        })}
      </FlagGroup>
    </FlagContext.Provider>
  );
};
